import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { getApi, postApi, getCurrencies } from '../services/apiServices';
import { secureStorage } from '../Utils/encryption';
import { recalculateCartItemsInNewCurrency } from '../Utils/helper';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { MAX_ESIM_COUNT } from '../Utils/constants';
import { getDestinationCardImage, getDestinationHeroImage } from '../Utils/destinationImages';
import { CheckIcon, CartIcon, BoltIcon, SignalIcon, PhoneIcon, GlobeIcon, StarIcon, HeadsetIcon, ShieldIcon, UnlimitedIcon, DataStackIcon } from './r4l/icons';
import SecurePayments from './r4l/SecurePayments';
import Scribble from './r4l/Scribble';
import '../styles/r4l-home.css';
import '../styles/r4l-destination.css';

const BUNDLE_TIERS = [1, 5, 10, 30, 50];
const UNLIMITED_TIERS = [1, 3, 5, 7, 10, 15, 30];

const parseNumericString = (str) => {
  if (!str) return 0;
  return parseFloat(str.toString().replace(/,/g, '')) || 0;
};

// Language mapping function to convert app language codes to API language codes
const getApiLanguageCode = (appLanguage) => {
  const languageMap = {
    'ar': 'ar',     // Arabic
    'da': 'da',     // Danish
    'de': 'de',     // German
    'en': 'en',     // English
    'es': 'es',     // Spanish
    'fr': 'fr',     // French
    'he': 'he',     // Hebrew
    'it': 'it',     // Italian
    'ja': 'ja',     // Japanese
    'ko': 'ko',     // Korean
    'nl': 'nl',     // Dutch
    'no': 'no',     // Norwegian
    'pl': 'pl',     // Polish
    'pt': 'pt',     // Portuguese
    'ru': 'ru',     // Russian
    'sv': 'sv',     // Swedish
    'tr': 'tr',     // Turkish
    'zh': 'zh_CN'   // Chinese (Simplified)
  };
  return languageMap[appLanguage] || 'en'; // Default to English if not found
};

function DestinationDetail() {
  const { iso3 } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [activeTab, setActiveTab] = useState('Data Bundles');
  const [gbCount, setGbCount] = useState(1);
  const [loading, setIsLoading] = useState(true);
  const [countryDetail, setCountryDetail] = useState(null);
  const [error, setError] = useState(null);
  const [daysCount, setDaysCount] = useState(1);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [esimCount, setEsimCount] = useState(1);
  const [price, setPrice] = useState(6.99);
  const [currentCountryData, setCurrentCountryData] = useState(null);
  const [mobileDescriptionTab, setMobileDescriptionTab] = useState('Features');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const [openFaqItems, setOpenFaqItems] = useState(new Set());
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [compatibilityData, setCompatibilityData] = useState([]);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isShowingChildDevices, setIsShowingChildDevices] = useState(false);
  const [deviceNavigationPath, setDeviceNavigationPath] = useState([]);
  const [calculatedChargeData, setCalculatedChargeData] = useState(null);
  const [unlimitedDaysCount, setUnlimitedDaysCount] = useState(1);
  const [tabLoading, setTabLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const isTabChanging = useRef(false);
  const debounceTimer = useRef(null);
  const currentRequest = useRef(null);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [currenciesError, setCurrenciesError] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('Currency');
  const [userChangedCurrency, setUserChangedCurrency] = useState(false);
  const [unlimitedPlansAvailable, setUnlimitedPlansAvailable] = useState(true);
  const [isUnlimitedOnly, setIsUnlimitedOnly] = useState(false);
  const [userChangedTab, setUserChangedTab] = useState(false);

  const getApiLanguageCode = (appLanguage) => {
    const languageMap = {
      'ar': 'ar',     // Arabic
      'da': 'da',     // Danish
      'de': 'de',     // German
      'en': 'en',     // English
      'es': 'es',     // Spanish
      'fr': 'fr',     // French
      'he': 'he',     // Hebrew
      'it': 'it',     // Italian
      'ja': 'ja',     // Japanese
      'ko': 'ko',     // Korean
      'nl': 'nl',     // Dutch
      'no': 'no',     // Norwegian
      'pl': 'pl',     // Polish
      'pt': 'pt',     // Portuguese
      'ru': 'ru',     // Russian
      'sv': 'sv',     // Swedish
      'tr': 'tr',     // Turkish
      'zh': 'zh_CN'   // Chinese (Simplified)
    };
    return languageMap[appLanguage] || 'en'; // Default to English if not found
  };

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      border: 'none',
      borderRadius: '10px',
      padding: '0',
      maxWidth: '600px',
      width: '90%',
      backgroundColor: 'transparent',
      zIndex: 1001,
      position: 'relative'
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }
  };

  const openDeviceModal = async () => {
    setShowDeviceModal(true);
    setSelectedDevice(null);
    setIsShowingChildDevices(false);
    setDeviceNavigationPath([]);
    await fetchCompatibilityData();
  };

  const useClickOutside = (ref, callback) => {
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (ref.current && !ref.current.contains(event.target)) {
          callback();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, callback]);
  };

  const closeDeviceModal = () => {
    setShowDeviceModal(false);
    setSelectedDevice(null);
    setIsShowingChildDevices(false);
    setDeviceNavigationPath([]);
    setCompatibilityData([]);
    setCompatibilityLoading(false);
  };



  const currencyDropdownRef = useRef(null);
  useClickOutside(currencyDropdownRef, () => {
    setShowCurrencyDropdown(false);
  });

  const toggleFaqItem = (itemId) => {
    const newOpenItems = new Set(openFaqItems);
    if (newOpenItems.has(itemId)) {
      newOpenItems.delete(itemId);
    } else {
      newOpenItems.add(itemId);
    }
    setOpenFaqItems(newOpenItems);
  };

  const fetchCompatibilityData = async () => {
    try {
      setCompatibilityLoading(true);
      const response = await getApi('/api/compatibilities');

      if (response?.status) {
        setCompatibilityData(response?.data || []);
      } else {
        console.error('Failed to fetch compatibility data:', response?.msg);
        toast.error(t('destinationDetail.errors.failedToLoadCompatibility'));
      }
    } catch (error) {
      console.error('Error fetching compatibility data:', error);
      toast.error(t('destinationDetail.errors.failedToLoadCompatibility'));
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const fetchChildDevices = async (parentId) => {
    try {
      setCompatibilityLoading(true);
      const response = await getApi(`/api/compatibilities/by-parent?parent_id=${parentId}`);

      if (response?.status) {
        setCompatibilityData(response?.data || []);
        setIsShowingChildDevices(true);
      } else {
        console.error('Failed to fetch child devices:', response?.msg);
        toast.error(t('destinationDetail.errors.failedToLoadChildDevices'));
      }
    } catch (error) {
      console.error('Error fetching child devices:', error);
      toast.error(t('destinationDetail.errors.failedToLoadChildDevices'));
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const handleDeviceClick = async (device) => {
    await fetchChildDevices(device.id);
    setDeviceNavigationPath(prev => [...prev, device]);
    setSelectedDevice(device);
  };

  const handleBackToParent = async () => {
    if (deviceNavigationPath.length > 1) {
      const newPath = deviceNavigationPath.slice(0, -1);
      const previousDevice = newPath[newPath.length - 1];

      setDeviceNavigationPath(newPath);
      setSelectedDevice(previousDevice);

      await fetchChildDevices(previousDevice.id);
    } else {
      setSelectedDevice(null);
      setIsShowingChildDevices(false);
      setDeviceNavigationPath([]);
      await fetchCompatibilityData();
    }
  };

  const handleBackToRoot = async () => {
    setSelectedDevice(null);
    setIsShowingChildDevices(false);
    setDeviceNavigationPath([]);
    await fetchCompatibilityData();
  };

  const handleDeviceSelection = async (device) => {
    try {
      setCompatibilityLoading(true);
      const response = await getApi(`/api/compatibilities/by-parent?parent_id=${device.id}`);

      if (response?.status && response?.data && response.data.length > 0) {
        const firstItem = response.data[0];
        if (firstItem.description && firstItem.name === null) {
          setCompatibilityData([firstItem]);
          setIsShowingChildDevices(true);
          setDeviceNavigationPath(prev => [...prev, device]);
          setSelectedDevice(device);
        } else {
          await handleDeviceClick(device);
        }
      } else {
        closeDeviceModal();
      }
    } catch (error) {
      console.error('Error checking device children:', error);
      closeDeviceModal();
    } finally {
      setCompatibilityLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (currentRequest.current) {
        currentRequest.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (showDeviceModal) {
        setShowDeviceModal(false);
        setSelectedDevice(null);
        setIsShowingChildDevices(false);
        setDeviceNavigationPath([]);
        setCompatibilityData([]);
        setCompatibilityLoading(false);
      }
    };
  }, [showDeviceModal]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showDeviceModal) {
        closeDeviceModal();
      }
    };

    if (showDeviceModal) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showDeviceModal]);

  useEffect(() => {
    fetchCountryData();
  }, [iso3, currentLanguage]);

  useEffect(() => {
    if (currentCountryData && userChangedCurrency) {
      fetchCountryData();
    }
  }, [selectedCurrency, userChangedCurrency]);

  useEffect(() => {
    if (currentCountryData && !isTabChanging.current) {

      if (activeTab === 'Unlimited Plans') {
        const initializeUnlimitedPlan = async () => {
          try {
            if (currentRequest.current) {
              currentRequest.current.abort();
            }

            const abortController = new AbortController();
            currentRequest.current = abortController;

            setPriceLoading(true);

            const payload = {
              days: daysCount || 1,
              esim: esimCount || 1,
              type: "unlimited",
              country_code: currentCountryData?.iso3 || iso3,
              currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency),
              lang: getApiLanguageCode(currentLanguage)
            };

            const response = await postApi('/api/calculation-of-plans', payload, { signal: abortController.signal });

            if (abortController.signal.aborted) {
              return;
            }

            if (response?.status) {
              const data = response.data;

              const chargeData = {
                total: data.total_for_one_esim || data.final_total,
                charges: data.charges
              };

              setCalculatedChargeData(chargeData);
              setPrice(Math.round(parseNumericString(data.final_total) * 100) / 100);
              setDaysCount(parseInt(data.no_of_days) || (daysCount || 1));
            } else {
              const basePrice = parseFloat(currentCountryData?.lowest_rate) || 6.99;
              const totalPrice = basePrice * (daysCount || 1) * (esimCount || 1);

              const mockChargeData = {
                total: (basePrice * (daysCount || 1)).toFixed(2),
                charges: basePrice.toFixed(2)
              };

              setCalculatedChargeData(mockChargeData);
              setPrice(Math.round(totalPrice * 100) / 100);
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              return;
            }

            const basePrice = parseFloat(currentCountryData?.lowest_rate) || 6.99;
            const totalPrice = basePrice * (daysCount || 1) * (esimCount || 1);

            const mockChargeData = {
              total: (basePrice * (daysCount || 1)).toFixed(2),
              charges: basePrice.toFixed(2)
            };

            setCalculatedChargeData(mockChargeData);
            setPrice(Math.round(totalPrice * 100) / 100);
          } finally {
            if (currentRequest.current && !currentRequest.current.signal.aborted) {
              setPriceLoading(false);
              setTabLoading(false);
            }
          }
        };

        initializeUnlimitedPlan();
      } else if (activeTab === 'Data Bundles') {
        calculatePrice(gbCount, esimCount, 'bundle');
      } else if (activeTab === 'Global eSIM') {
        calculatePrice(gbCount, esimCount, 'global');
      }
    }
  }, [currentCountryData]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      setCurrenciesLoading(true);
      setCurrenciesError(null);
      try {
        const response = await getCurrencies();
        if (response?.status && Array.isArray(response.data)) {
          setCurrencies(response.data);

        } else {
          setCurrencies([]);
          setCurrenciesError(t('destinationDetail.errors.failedToFetchCurrencies'));
        }
      } catch (err) {
        setCurrencies([]);
        setCurrenciesError(t('destinationDetail.errors.failedToFetchCurrencies'));
      } finally {
        setCurrenciesLoading(false);
      }
    };
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const storedCurrency = localStorage.getItem('selectedCurrency');
    if (storedCurrency && userChangedCurrency) {
      setSelectedCurrency(storedCurrency);
    }
  }, [userChangedCurrency]);

  useEffect(() => {
    if (currentCountryData?.currency && !userChangedCurrency) {
      setSelectedCurrency(currentCountryData.currency);
    }
  }, [currentCountryData, userChangedCurrency]);

  const fetchCountryData = async () => {
    const startTime = Date.now();
    const MIN_LOADING_TIME = 300; // Minimum loading time in milliseconds

    try {
      setIsLoading(true);
      setError(null);

      const currentCurrency = localStorage.getItem('selectedCurrency') || 'USD';
      const apiLanguage = getApiLanguageCode(currentLanguage);

      const currencyParam = currentCurrency !== 'USD' ? `currency=${currentCurrency}` : '';
      const langParam = `lang=${apiLanguage}`;

      let queryParams = [];
      if (currencyParam) queryParams.push(currencyParam);
      if (langParam) queryParams.push(langParam);

      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      const response = await getApi(`/api/country/${iso3}${queryString}`);

      if (response?.status) {
        const countryData = response.data.country;

        setUnlimitedPlansAvailable(countryData.unlimited_plan_status === 'available');

        // Regions (e.g. Europe) carry a supported_countries list and are sold as Unlimited only.
        const regionOnly = Boolean(countryData.supported_countries) && countryData.unlimited_plan_status === 'available';
        setIsUnlimitedOnly(regionOnly);

        if (regionOnly) {
          setActiveTab('Unlimited Plans');
        }
        else if (countryData.unlimited_plan_status !== 'available' && activeTab === 'Unlimited Plans') {
          setActiveTab('Data Bundles');
        }
        else if (countryData.unlimited_plan_status === 'available' && activeTab === 'Data Bundles' && !userChangedTab) {
          setActiveTab('Unlimited Plans');
        }

        setCurrentCountryData({
          name: countryData.name || 'Unknown Country',
          iso3: countryData.iso3 || iso3,
          lowest_rate: countryData.lowest_rate || countryData.price || 6.99,
          flag: countryData.flag || 'images/sim1.svg',
          symbol: countryData.currency_symbol || '$',
          currency: countryData.currency || 'USD'
        });
      } else {
        const fallbackCountryData = {
          name: 'Unknown Country',
          iso3: iso3,
          lowest_rate: 6.99,
          flag: 'images/sim1.svg'
        };
        setCurrentCountryData(fallbackCountryData);
        setError(t('destinationDetail.errors.failedToLoadCountryData'));
        setUnlimitedPlansAvailable(true);
      }
    } catch (error) {
      console.error('Error fetching country data:', error);

      const fallbackCountryData = {
        name: 'Unknown Country',
        iso3: iso3,
        lowest_rate: 6.99,
        flag: 'images/sim1.svg'
      };
      setCurrentCountryData(fallbackCountryData);
      setError(t('destinationDetail.errors.failedToLoadCountryData'));
      setUnlimitedPlansAvailable(true);
    } finally {
      // Delay requested by QA, minimum loading time to prevent flash on fast connections
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
      
      if (remainingTime > 0) {
        setTimeout(() => {
          setIsLoading(false);
        }, remainingTime);
      } else {
        setIsLoading(false);
      }
    }
  };

  const resetInputs = () => {
    setGbCount(1);
    setDaysCount(activeTab === 'Unlimited Plans' ? 1 : 30);
    setDateRange([null, null]);
    setEsimCount(1);
    setCalculatedChargeData(null);
    setUnlimitedDaysCount(1);

    if (activeTab === 'Data Bundles') {
      calculatePrice(1, 1, 'bundle', { silent: true });
    } else if (activeTab === 'Global eSIM') {
      calculatePrice(1, 1, 'global', { silent: true });
    } else if (activeTab === 'Unlimited Plans') {
      const resetUnlimitedPlan = async () => {
        try {
          if (currentRequest.current) {
            currentRequest.current.abort();
          }

          const abortController = new AbortController();
          currentRequest.current = abortController;

          setPriceLoading(true);

          const payload = {
            days: 1,
            esim: 1,
            type: "unlimited",
            country_code: currentCountryData?.iso3 || iso3,
            currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency),
            lang: getApiLanguageCode(currentLanguage)
          };

          const response = await postApi('/api/calculation-of-plans', payload, { signal: abortController.signal });

          if (abortController.signal.aborted) {
            return;
          }

          if (response?.status) {
            const data = response.data;


            const chargeData = {
              total: data.total_for_one_esim || data.final_total,
              charges: data.charges
            };

            setCalculatedChargeData(chargeData);
            setPrice(Math.round(parseNumericString(data.final_total) * 100) / 100);
            setDaysCount(parseInt(data.no_of_days) || 1);
          } else {

            const basePrice = parseFloat(currentCountryData?.lowest_rate) || 6.99;
            const totalPrice = basePrice * 1 * 1;

            const mockChargeData = {
              total: (basePrice * 1).toFixed(2),
              charges: basePrice.toFixed(2)
            };

            setCalculatedChargeData(mockChargeData);
            setPrice(Math.round(totalPrice * 100) / 100);
            setDaysCount(1);
          }
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }

          const basePrice = parseFloat(currentCountryData?.lowest_rate) || 6.99;
          const totalPrice = basePrice * 1 * 1;

          const mockChargeData = {
            total: (basePrice * 1).toFixed(2),
            charges: basePrice.toFixed(2)
          };

          setCalculatedChargeData(mockChargeData);
          setPrice(Math.round(totalPrice * 100) / 100);
          setDaysCount(1);
        } finally {
          if (currentRequest.current && !currentRequest.current.signal.aborted) {
            setPriceLoading(false);
            setTabLoading(false);
          }
        }
      };

      resetUnlimitedPlan();
    } else {
      const basePrice = parseFloat(currentCountryData?.lowest_rate) || 6.99;
      setPrice(Math.round((basePrice) * 100) / 100);
    }
  };

  const handleSubmit = async () => {
    try {
      if (activeTab === 'Unlimited Plans' && (!unlimitedDaysCount || unlimitedDaysCount === 0)) {
        toast.error(t('destinationDetail.errors.selectDuration', 'Please select a duration'));
        return;
      }

      setIsLoading(true);

      let apiResponseData = null;

      if (activeTab === 'Data Bundles') {
        const response = await postApi('/api/calculation-of-plans', {
          gb: gbCount,
          esim: esimCount,
          lowest_rate: parseFloat(currentCountryData?.lowest_rate) || 6.99,
          type: 'bundle',
          country_code: currentCountryData?.iso3 || iso3,
          currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency),
          lang: getApiLanguageCode(currentLanguage)
        });
        apiResponseData = response;
      } else if (activeTab === 'Global eSIM') {
        const response = await postApi('/api/calculation-of-plans', {
          gb: gbCount,
          esim: esimCount,
          lowest_rate: parseFloat(currentCountryData?.lowest_rate) || 6.99,
          type: 'global',
          country_code: currentCountryData?.iso3 || iso3,
          currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency),
          lang: getApiLanguageCode(currentLanguage)
        });
        apiResponseData = response;
      } else if (activeTab === 'Unlimited Plans') {
        const response = await postApi('/api/calculation-of-plans', {
          days: daysCount,
          esim: esimCount,
          type: "unlimited",
          country_code: currentCountryData?.iso3 || iso3,
          currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency),
          lang: getApiLanguageCode(currentLanguage)
        });
        apiResponseData = response;
      }

      if (!apiResponseData?.status) {
        toast.error(t('destinationDetail.errors.failedToCalculatePlan'));
        return;
      }

      const payload = {
        plan_type: activeTab,
        country: currentCountryData?.name || "United States",
        days: daysCount,
        esim_count: esimCount,
        price: price,
        lowest_rate: parseFloat(currentCountryData?.lowest_rate) || 6.99,
        gb: (activeTab === 'Data Bundles' || activeTab === 'Global eSIM') ? gbCount : null,
        country_code: currentCountryData?.iso3 || iso3,
        iso3: currentCountryData?.iso3 || iso3,
        flag: currentCountryData?.flag || null,
        startDate: (activeTab === 'Unlimited Plans' && dateRange && dateRange[0]) ? dateRange[0].toISOString() : null,
        endDate: (activeTab === 'Unlimited Plans' && dateRange && dateRange[1]) ? dateRange[1].toISOString() : null,
        api_response: apiResponseData.data,
        calculated_at: new Date().toISOString(),
        calculation_hash: btoa(`${activeTab}-${gbCount}-${esimCount}-${daysCount}-${currentCountryData?.iso3}`),
        currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency)
      };


      const existingData = secureStorage.getItem('checkoutSession') || [];


      const existingIndex = existingData.findIndex(item => {
        const basicMatch = item.plan_type === activeTab && item.days === daysCount;

        if (activeTab === 'Data Bundles' || activeTab === 'Global eSIM') {
          return basicMatch && item.gb === gbCount && item.country === payload.country;
        }

        if (activeTab === 'Unlimited Plans') {
          return basicMatch && item.country === payload.country && item.startDate === payload.startDate;
        }

        return basicMatch && item.country === payload.country;
      });



      if (existingIndex !== -1) {

        const existingItem = existingData[existingIndex];
        const newTotalEsimCount = (existingItem.esim_count || 1) + esimCount;
        existingItem.esim_count = newTotalEsimCount;

        let newTotalPrice = 0;
        if (activeTab === 'Data Bundles' || activeTab === 'Global eSIM') {
          const pricePerEsim = price / esimCount;
          newTotalPrice = Math.round((pricePerEsim * newTotalEsimCount) * 100) / 100;
        } else if (activeTab === 'Unlimited Plans') {
          const pricePerEsim = price / esimCount;
          newTotalPrice = Math.round((pricePerEsim * newTotalEsimCount) * 100) / 100;
        }

        existingItem.price = newTotalPrice;
        existingItem.lowest_rate = parseFloat(currentCountryData?.lowest_rate) || 6.99;
        existingItem.startDate = (activeTab === 'Unlimited Plans' && dateRange && dateRange[0]) ? dateRange[0].toISOString() : null;
        existingItem.endDate = (activeTab === 'Unlimited Plans' && dateRange && dateRange[1]) ? dateRange[1].toISOString() : null;
        existingItem.api_response = apiResponseData.data;
        existingItem.calculated_at = new Date().toISOString();
        existingItem.calculation_hash = btoa(`${activeTab}-${gbCount}-${existingItem.esim_count}-${daysCount}-${currentCountryData?.iso3}`);
        existingItem.currency = selectedCurrency;
        existingItem.calculation_hash = btoa(`${activeTab}-${gbCount}-${existingItem.esim_count}-${daysCount}-${currentCountryData?.iso3}`);
        existingItem.currency = userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency);
        existingData[existingIndex] = existingItem;
      } else {

        existingData.push(payload);
      }

      secureStorage.setItem('checkoutSession', existingData);


      if (existingIndex !== -1) {

      }

      setTimeout(() => {
        window.dispatchEvent(new Event('cartUpdated'));
      }, 100);

      toast.success(t('destinationDetail.success.productAddedToCart'));
      resetInputs();
      navigate('/checkout');
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error(t('destinationDetail.errors.errorAddingToCart'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    if (activeTab === tab) {
      return;
    }

    if (tab === 'Unlimited Plans' && !unlimitedPlansAvailable) {

      return;
    }

    setActiveTab(tab);
    setUserChangedTab(true);
    setTabLoading(true);
    isTabChanging.current = true;

    setGbCount(1);
    setDateRange([null, null]);
    setEsimCount(1);
    setCalculatedChargeData(null);
    setUnlimitedDaysCount(1);

    if (tab === 'Data Bundles') {

      calculatePrice(1, 1, 'bundle').finally(() => {

        isTabChanging.current = false;
      });
    } else if (tab === 'Global eSIM') {

      calculatePrice(1, 1, 'global').finally(() => {

        isTabChanging.current = false;
      });
    } else if (tab === 'Unlimited Plans') {

      calculateCharge(1).finally(() => {

        isTabChanging.current = false;
      });
    }
  };
  // "Most popular" cards under the FAQ (same API as the home page).
  const [moreCountries, setMoreCountries] = useState([]);
  useEffect(() => {
    const currentCurrency = localStorage.getItem('selectedCurrency') || 'USD';
    const currencyParam = currentCurrency !== 'USD' ? `&currency=${currentCurrency}` : '';
    getApi(`/api/countries?count=6&page=1&filter=popular${currencyParam}&lang=${getApiLanguageCode(currentLanguage)}`)
      .then((response) => {
        setMoreCountries(response?.data?.countries || []);
      })
      .catch(() => setMoreCountries([]));
  }, [currentLanguage, selectedCurrency]);

  const handleMobileDescriptionTabChange = (tab) => {
    setMobileDescriptionTab(tab);
  };

  const calculatePrice = async (gb, esim, type, { silent = false } = {}) => {
    try {
      if (currentRequest.current) {
        currentRequest.current.abort();
      }

      const abortController = new AbortController();
      currentRequest.current = abortController;

      setPriceLoading(true);
      const payload = {
        gb: gb,
        esim: esim,
        lowest_rate: parseFloat(currentCountryData?.lowest_rate) || 6.99,
        type: type,
        country_code: currentCountryData?.iso3 || iso3,
        currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency),
        lang: getApiLanguageCode(currentLanguage)
      };

      const response = await postApi('/api/calculation-of-plans', payload, { signal: abortController.signal });

      if (abortController.signal.aborted) {
        return;
      }

      if (response?.status) {

        const data = response.data;

        if (data.no_of_days) {
          const daysFromApi = parseInt(data.no_of_days);

          setDaysCount(daysFromApi);
        }

        if (data.final_total) {
          setPrice(Math.round(parseNumericString(data.final_total) * 100) / 100);
        }
      } else {

        if (!silent) {
          toast.error(t('destinationDetail.errors.failedToCalculatePlan'));
        }
        const basePricePerGb = parseFloat(currentCountryData?.lowest_rate) || 6.99;
        const gbCost = basePricePerGb * gb;
        const esimCost = basePricePerGb * esim;
        setPrice(Math.round((gbCost + esimCost) * 100) / 100);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      if (!silent) {
        toast.error(t('destinationDetail.errors.failedToCalculatePlan'));
      }
      const basePricePerGb = parseFloat(currentCountryData?.lowest_rate) || 6.99;
      const gbCost = basePricePerGb * gb;
      const esimCost = basePricePerGb * esim;
      setPrice(Math.round((gbCost + esimCost) * 100) / 100);
    } finally {
      if (currentRequest.current && !currentRequest.current.signal.aborted) {
        setPriceLoading(false);
        setTabLoading(false);
      }
    }
  };

  const calculatePriceForUnlimited = async (esimCount) => {
    try {
      if (currentRequest.current) {
        currentRequest.current.abort();
      }

      const abortController = new AbortController();
      currentRequest.current = abortController;

      setPriceLoading(true);
      const payload = {
        days: daysCount,
        esim: esimCount,
        type: "unlimited",
        country_code: currentCountryData?.iso3 || iso3,
        currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency),
        lang: getApiLanguageCode(currentLanguage)
      };

      const response = await postApi('/api/calculation-of-plans', payload, { signal: abortController.signal });

      if (abortController.signal.aborted) {
        return;
      }

      if (response?.status) {

        const data = response.data;

        if (data.final_total) {
          setPrice(Math.round(parseNumericString(data.final_total) * 100) / 100);
        }
        if (data.no_of_days) {
          const daysFromApi = parseInt(data.no_of_days);

          setDaysCount(daysFromApi);
        }
      } else {

        toast.error(t('destinationDetail.errors.failedToCalculateUnlimitedPlan'));
        const basePricePerEsim = parseFloat(currentCountryData?.lowest_rate) || 6.99;
        setPrice(Math.round((basePricePerEsim * esimCount * daysCount) * 100) / 100);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      toast.error(t('destinationDetail.errors.failedToCalculateUnlimitedPlan'));
      const basePricePerEsim = parseFloat(currentCountryData?.lowest_rate) || 6.99;
      setPrice(Math.round((basePricePerEsim * esimCount * daysCount) * 100) / 100);
    } finally {
      if (currentRequest.current && !currentRequest.current.signal.aborted) {
        setPriceLoading(false);
        setTabLoading(false);
      }
    }
  };

  const handleGbChange = (type) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    const currentIndex = BUNDLE_TIERS.indexOf(gbCount);
    let newIndex = currentIndex !== -1 ? currentIndex : 0;

    if (type === 'increment') {
      if (newIndex < BUNDLE_TIERS.length - 1) {
        newIndex += 1;
      }
    } else if (type === 'decrement') {
      if (newIndex > 0) {
        newIndex -= 1;
      }
    }

    const newGbCount = BUNDLE_TIERS[newIndex];
    setGbCount(newGbCount);

    debounceTimer.current = setTimeout(() => {
      if (activeTab === 'Data Bundles') {
        calculatePrice(newGbCount, esimCount, 'bundle');
      } else if (activeTab === 'Global eSIM') {
        calculatePrice(newGbCount, esimCount, 'global');
      }
    }, 300);
  };

  const handleDaysChange = (type) => {
    if (type === 'increment' && daysCount < 90) {
      setDaysCount(daysCount + 1);
    } else if (type === 'decrement' && daysCount > 1) {
      setDaysCount(daysCount - 1);
    }
  };

  const handleUnlimitedTierSelect = (tier) => {
    if (priceLoading) return;
    setUnlimitedDaysCount(tier);
    setDaysCount(tier);
    if (dateRange && dateRange[0]) {
      const end = dateRange[0].add(tier - 1, 'day');
      setDateRange([dateRange[0], end]);
    }
    calculateCharge(tier);
  };

  const handleEsimChange = (type) => {

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (type === 'increment' && esimCount < MAX_ESIM_COUNT) {
        const newEsimCount = esimCount + 1;
        setEsimCount(newEsimCount);

        if (activeTab === 'Data Bundles') {
          calculatePrice(gbCount, newEsimCount, 'bundle');
        } else if (activeTab === 'Global eSIM') {
          calculatePrice(gbCount, newEsimCount, 'global');
        } else if (activeTab === 'Unlimited Plans') {
          calculatePriceForUnlimited(newEsimCount);
        }
      } else if (type === 'decrement' && esimCount > 1) {
        const newEsimCount = esimCount - 1;
        setEsimCount(newEsimCount);

        if (activeTab === 'Data Bundles') {
          calculatePrice(gbCount, newEsimCount, 'bundle');
        } else if (activeTab === 'Global eSIM') {
          calculatePrice(gbCount, newEsimCount, 'global');
        } else if (activeTab === 'Unlimited Plans') {
          calculatePriceForUnlimited(newEsimCount);
        }
      }
    }, 300);
  };

  const handleGbInputChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    if (BUNDLE_TIERS.includes(value)) {
      setGbCount(value);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        if (activeTab === 'Data Bundles') {
          calculatePrice(value, esimCount, 'bundle');
        } else if (activeTab === 'Global eSIM') {
          calculatePrice(value, esimCount, 'global');
        }
      }, 500);
    }
  };

  const handleDaysInputChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    if (value >= 1 && value <= 90) {
      setDaysCount(value);
    }
  };

  const handleEsimInputChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    if (value >= 1 && value <= MAX_ESIM_COUNT) {
      setEsimCount(value);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        if (activeTab === 'Data Bundles') {
          calculatePrice(gbCount, value, 'bundle');
        } else if (activeTab === 'Global eSIM') {
          calculatePrice(gbCount, value, 'global');
        } else if (activeTab === 'Unlimited Plans') {
          calculatePriceForUnlimited(value);
        }
      }, 500);
    }
  };

  const handleStartDateChange = (date) => {
    if (date) {
      const start = date.startOf('day');
      const end = start.add(unlimitedDaysCount - 1, 'day');
      setDateRange([start, end]);
    } else {
      setDateRange([null, null]);
    }
  };

  const calculateCharge = async (days) => {
    try {

      if (currentRequest.current) {
        currentRequest.current.abort();
      }
      const abortController = new AbortController();
      currentRequest.current = abortController;

      setPriceLoading(true);
      console.log('calculateCharge called with days:', days, 'activeTab:', activeTab);

      const effectiveDays = Math.max(1, days);

      const payload = {
        days: effectiveDays,
        esim: esimCount,
        type: "unlimited",
        country_code: currentCountryData?.iso3 || iso3,
        currency: userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency)
      };

      const response = await postApi('/api/calculation-of-plans', payload, { signal: abortController.signal });


      if (abortController.signal.aborted) {
        return;
      }

      if (response?.status) {
        const data = response.data;

        const chargeData = {
          total: data.total_for_one_esim || data.final_total,
          charges: data.charges
        };


        setCalculatedChargeData(chargeData);
        setPrice(Math.round(parseNumericString(data.final_total) * 100) / 100);
        setDaysCount(parseInt(data.no_of_days) || effectiveDays);

      } else {


        const basePrice = parseFloat(currentCountryData?.lowest_rate) || 6.99;
        const totalPrice = basePrice * effectiveDays * esimCount;

        const mockChargeData = {
          total: (basePrice * effectiveDays).toFixed(2),
          charges: basePrice.toFixed(2)
        };


        setCalculatedChargeData(mockChargeData);
        setPrice(Math.round(totalPrice * 100) / 100);
        setDaysCount(effectiveDays);
      }
    } catch (error) {

      if (error.name === 'AbortError') {
        return;
      }
      console.error('Error calculating charge:', error);

      const basePrice = parseFloat(currentCountryData?.lowest_rate) || 6.99;
      const totalPrice = basePrice * Math.max(1, days) * esimCount;

      const mockChargeData = {
        total: (basePrice * Math.max(1, days)).toFixed(2),
        charges: basePrice.toFixed(2)
      };


      setCalculatedChargeData(mockChargeData);
      setPrice(Math.round(totalPrice * 100) / 100);
      setDaysCount(Math.max(1, days));
    } finally {

      if (currentRequest.current && !currentRequest.current.signal.aborted) {
        setPriceLoading(false);
        setTabLoading(false);
      }
    }
  };

  const countryName = currentCountryData?.name || 'USA';
  const countryIso3 = currentCountryData?.iso3 || iso3;
  const activeCurrencyCode = userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || selectedCurrency);
  const activeCurrencySymbol = currencies.find(c => c.code === activeCurrencyCode)?.symbol || (currentCountryData?.symbol || '$');
  const showTotal = !(activeTab === 'Unlimited Plans' && unlimitedDaysCount < 1);
  const addDisabled = activeTab === 'Unlimited Plans' && unlimitedDaysCount === 0;
  const flagSrc = currentCountryData?.flag || window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg');

  // The full-speed promise applies to Data Bundles only; Unlimited and Universal eSIM show their own speed rules.
  const features = activeTab === 'Data Bundles'
    ? ['highSpeedData', 'noThrottling', 'instantActivation', 'reliableCoverage', 'keepWhatsApp', 'noRoamingFees']
    : ['instantActivation', 'reliableCoverage', 'keepWhatsApp', 'noRoamingFees'];

  const faqItems = [
    { id: 'detail-faq-1', q: t('destinationDetail.faq.question1'), a: t('destinationDetail.faq.answer1') },
    { id: 'detail-faq-2', q: t('destinationDetail.faq.question2'), a: <>{t('destinationDetail.faq.answer2')} <a onClick={openDeviceModal}>{t('destinationDetail.faq.checkCompatibleDevices')}</a></> },
    { id: 'detail-faq-3', q: t('destinationDetail.faq.question3'), a: t('destinationDetail.faq.answer3') },
    { id: 'detail-faq-4', q: t('destinationDetail.faq.question4'), a: t('destinationDetail.faq.answer4') },
    { id: 'detail-faq-5', q: t('destinationDetail.faq.question5'), a: t('destinationDetail.faq.answer5') },
    { id: 'detail-faq-6', q: t('destinationDetail.faq.question6'), a: t('destinationDetail.faq.answer6') },
    { id: 'detail-faq-7', q: t('destinationDetail.faq.question7'), a: <>{t('destinationDetail.faq.answer7')} <Link to="/refund-policy">{t('destinationDetail.faq.seeRefundPolicy')}</Link></> },
    {
      id: 'detail-faq-8',
      q: currentCountryData?.name === 'USA' ? t('destinationDetail.faq.question8USA') : t('destinationDetail.faq.question8Other', { country: currentCountryData?.name || 'the country' }),
      a: t('destinationDetail.faq.answer8')
    }
  ];

  const renderFaq = (item) => {
    const isOpen = openFaqItems.has(item.id);
    return (
      <div className="r4l-faq-item" key={item.id}>
        <button
          type="button"
          className="r4l-faq-q"
          onClick={() => toggleFaqItem(item.id)}
          aria-expanded={isOpen}
          aria-controls={`${item.id}-answer`}
        >
          {item.q}
        </button>
        {isOpen && <div className="r4l-faq-a" id={`${item.id}-answer`}>{item.a}</div>}
      </div>
    );
  };

  // [key, label, subtitle, icon, tone class, feature bullets]
  const tabs = [
    unlimitedPlansAvailable && ['Unlimited Plans', t('destinationDetail.tabs.unlimitedPlans'), t('r4l.detail.tabUnlimited'), <UnlimitedIcon />, 'is-unlimited', [
      t('r4l.detail.cardUnlimited1'), t('r4l.detail.cardUnlimited2'), t('r4l.promise.icon3')
    ]],
    !isUnlimitedOnly && ['Data Bundles', t('destinationDetail.tabs.dataBundles'), t('r4l.detail.tabBundles'), <DataStackIcon />, 'is-bundles', [
      t('r4l.detail.cardBundles1'), t('r4l.detail.cardBundles2'), t('r4l.detail.cardBundles3')
    ]],
    !isUnlimitedOnly && ['Global eSIM', t('destinationDetail.tabs.globalESIM'), t('r4l.detail.tabGlobal'), <GlobeIcon />, 'is-global', [
      // Not the site-wide destination count: Universal eSIM coverage is its own list.
      t('r4l.plans.global.i3'),
      t('r4l.detail.cardGlobal2'), t('r4l.detail.cardGlobal3')
    ]]
  ].filter(Boolean);
  const activeTabIcon = tabs.find(([key]) => key === activeTab)?.[3];

  // Descriptions read "<title> - <tagline>" in every locale; show them as heading + subline.
  const planHeading = activeTab === 'Unlimited Plans'
    ? t('destinationDetail.descriptions.unlimitedPlans', { country: countryName })
    : activeTab === 'Data Bundles'
      ? t('destinationDetail.descriptions.dataBundles', { country: countryName })
      : t('destinationDetail.descriptions.globalESIM');
  const headingSplit = planHeading.lastIndexOf(' - ');
  const planTitle = headingSplit > 0 ? planHeading.slice(0, headingSplit) : planHeading;
  const planTagline = headingSplit > 0 ? planHeading.slice(headingSplit + 3) : '';

  const stepper =(value, onDecrement, onIncrement, decDisabled, incDisabled, inputProps = {}) => (
    <div className="r4l-stepper">
      <button type="button" onClick={onDecrement} disabled={decDisabled} aria-label="Decrease">−</button>
      <input type="text" value={value} {...inputProps} />
      <button type="button" onClick={onIncrement} disabled={incDisabled} aria-label="Increase">+</button>
    </div>
  );

  const priceText = (
    <>
      {activeCurrencySymbol}{(price || 0).toFixed(2)} <small>{userChangedCurrency ? selectedCurrency : (currentCountryData?.currency || 'USD')}</small>
    </>
  );

  return (
    <div className="r4l-home r4l-dest">
      {loading ? (
        <div className="r4l-dest-state"><span className="loader"></span></div>
      ) : error ? (
        <div className="r4l-dest-state"><h3>Error: {error}</h3></div>
      ) : (
        <>
          {/* Hero */}
          <section className="r4l-dhero">
            <img className="r4l-hero-bg" src={getDestinationHeroImage(countryIso3)} alt="" fetchpriority="high" />
            <div className="r4l-dhero-shade" />
            <div className="r4l-dhero-inner">
              <p className="r4l-place-kicker">
                <img
                  src={flagSrc}
                  alt=""
                  onError={(e) => { e.target.src = window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg'); }}
                />
                <span>{countryName}</span>
              </p>
              <h1>{t('destinationDetail.eSIMFor', { country: countryName })}</h1>
              <p className="r4l-dhero-sub">{t('r4l.detail.heroSub')}</p>
              <p className="r4l-dhero-sub">{t('r4l.detail.heroIntro')}</p>
              <ul className="r4l-chips">
                <li><CheckIcon />{t('destinationDetail.features.instantActivation')}</li>
                <li><CheckIcon />{t('destinationDetail.features.reliableCoverage')}</li>
                <li><CheckIcon />{t('destinationDetail.features.noRoamingFees')}</li>
              </ul>
              <button type="button" className="r4l-btn-premium r4l-compat-btn" onClick={openDeviceModal}>
                {t('destinationDetail.checkCompatibility')}<i aria-hidden="true"></i>
              </button>
            </div>
            <Scribble className="r4l-dhero-note" text={t('scribbles.destinationHero', { country: countryName })} />
          </section>

          {/* Plan panel */}
          <div className="r4l-wrap r4l-panel-wrap">
            <section className="r4l-panel" aria-label={t('destinationDetail.eSIMFor', { country: countryName })}>
              <div className="r4l-plan-head">
                <h2>{t('r4l.plans.kicker')}</h2>
                <p>{t('r4l.detail.chooseSub')}</p>
              </div>
              <div className={`r4l-tabs is-${tabs.length}`} role="tablist">
                {tabs.map(([key, label, sub, icon, tone, points]) => (
                  <button
                    key={key}
                    type="button"
                    className={tone}
                    role="tab"
                    aria-selected={activeTab === key}
                    disabled={tabLoading}
                    onClick={() => !tabLoading && handleTabChange(key)}
                  >
                    <span className="r4l-tab-icon">{icon}</span>
                    <span className="r4l-tab-mark" aria-hidden="true"><CheckIcon /></span>
                    <b>{label}</b>
                    <small>{sub}</small>
                    <span className="r4l-tab-points">
                      {points.map((point) => (
                        <span key={point}><CheckIcon />{point}</span>
                      ))}
                    </span>
                    <span className="r4l-tab-cta">
                      {activeTab === key ? t('r4l.detail.cardSelected') : t('r4l.detail.cardSelect')}
                    </span>
                  </button>
                ))}
              </div>

              <div className="r4l-panel-body">
                {tabLoading && (
                  <div className="r4l-panel-loading"><span className="loader"></span></div>
                )}

                <header className="r4l-plan-title">
                  <span className="r4l-plan-title-icon">{activeTabIcon}</span>
                  <div>
                    <h3>{planTitle}</h3>
                    {planTagline && <p>{planTagline}</p>}
                  </div>
                </header>

                <div className="r4l-panel-info">
                  <ul className="r4l-checks">
                    {features.map((key) => (
                      <li key={key}><CheckIcon />{t(`destinationDetail.features.${key}`)}</li>
                    ))}
                  </ul>

                  {/* Highlight box: our actual speed policy per plan type */}
                  {activeTab === 'Unlimited Plans' && (
                    <>
                      <aside className="r4l-side-note">
                        <strong>{t('r4l.detail.sideUnlimitedValue')}</strong>
                        <div>
                          <b>{t('r4l.detail.sideUnlimitedLabel')}</b>
                          <p>{t('speedPolicy.table.mayReduceAfter500MB')}</p>
                        </div>
                      </aside>
                      <Link className="r4l-policy-link" to="/speedPolicy">{t('header.nav.speedPolicy')} <span aria-hidden="true">→</span></Link>
                    </>
                  )}
                  {activeTab === 'Data Bundles' && (
                    <aside className="r4l-side-note">
                      <strong>100%</strong>
                      <div>
                        <b>{t('r4l.detail.sideBundleLabel')}</b>
                        <p>{t('destinationDetail.description.text3')}</p>
                      </div>
                    </aside>
                  )}
                </div>

                <div className="r4l-panel-pick">
                  {(activeTab === 'Data Bundles' || activeTab === 'Global eSIM') && (
                    <div className="r4l-field">
                      <label>{t('destinationDetail.form.numberOfGB')}</label>
                      <div className="r4l-pick">
                        {BUNDLE_TIERS.map((tier) => (
                          <button
                            key={tier}
                            type="button"
                            aria-pressed={gbCount === tier}
                            disabled={priceLoading}
                            onClick={() => gbCount !== tier && handleGbInputChange({ target: { value: tier } })}
                          >
                            {tier} GB
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'Unlimited Plans' && (
                    <div className="r4l-field">
                      <label>{t('destinationDetail.form.selectDuration', t('destinationDetail.form.numberOfDays', 'Select Duration'))}</label>
                      <div className="r4l-pick">
                        {UNLIMITED_TIERS.map((tier) => (
                          <button
                            key={tier}
                            type="button"
                            aria-pressed={unlimitedDaysCount === tier}
                            disabled={priceLoading}
                            onClick={() => handleUnlimitedTierSelect(tier)}
                          >
                            {tier} {tier === 1 ? t('destinationDetail.form.day', 'day') : t('cartDropdown.days', 'days')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="r4l-total">
                    {showTotal && (
                      priceLoading ? (
                        <p className="r4l-price is-loading">{t('destinationDetail.calculating')}</p>
                      ) : (
                        <p className="r4l-price">
                          {priceText}
                          {calculatedChargeData?.charges && parseFloat(calculatedChargeData.charges) > 0 && (
                            <span className="r4l-per-day"> / {activeCurrencySymbol}{parseFloat(calculatedChargeData.charges).toFixed(2)} {t('destinationDetail.form.perDay')}</span>
                          )}
                        </p>
                      )
                    )}
                  </div>
                </div>

                <div className="r4l-panel-buy">
                  {activeTab === 'Unlimited Plans' ? (
                    <>
                      <div className="r4l-field r4l-field-inline r4l-field-date">
                        <label>
                          {t('destinationDetail.form.startDate', 'Start Date')} <span className="r4l-optional">({t('destinationDetail.form.optional', 'Optional')})</span>
                        </label>
                        <DatePicker
                          className="unlimited-start-datepicker r4l-datepicker"
                          value={dateRange && dateRange[0] ? dateRange[0] : null}
                          onChange={handleStartDateChange}
                          allowClear={true}
                          placeholder={t('destinationDetail.form.selectStartDate', 'Select start date')}
                          disabledDate={(current) => current && current < dayjs().startOf('day')}
                          format="YYYY-MM-DD"
                        />
                      </div>
                      {dateRange && dateRange[0] && dateRange[1] ? (
                        <p className="r4l-hint">
                          <span className="r4l-hint-ok">✓</span> {t('destinationDetail.form.coveragePeriod', 'Coverage period')}: <strong>{dateRange[0].format('MMM DD, YYYY')}</strong> – <strong>{dateRange[1].format('MMM DD, YYYY')}</strong> ({unlimitedDaysCount} {unlimitedDaysCount === 1 ? t('destinationDetail.form.day', 'day') : t('cartDropdown.days', 'days')})
                        </p>
                      ) : (
                        <p className="r4l-hint">{t('destinationDetail.form.autoActivateNote', 'Optional: If no start date is selected, your eSIM activates automatically upon arrival.')}</p>
                      )}
                    </>
                  ) : (
                    <div className="r4l-field r4l-field-inline">
                      <label>{t('destinationDetail.form.numberOfDays')}</label>
                      <span className="r4l-static-value">{daysCount}</span>
                    </div>
                  )}

                  <div className="r4l-field r4l-field-inline">
                    <label>{t('destinationDetail.form.numberOfESIMs')}</label>
                    {stepper(
                      esimCount,
                      () => handleEsimChange('decrement'),
                      () => handleEsimChange('increment'),
                      esimCount <= 1 || priceLoading,
                      esimCount >= MAX_ESIM_COUNT || priceLoading,
                      { onChange: handleEsimInputChange, min: 1, max: MAX_ESIM_COUNT, disabled: priceLoading, 'aria-label': t('destinationDetail.form.numberOfESIMs') }
                    )}
                  </div>

                  {/* Currency (existing dropdown, restyled) */}
                  <div className="r4l-currency" ref={currencyDropdownRef}>
                    <button type="button" className="r4l-currency-btn" onClick={() => setShowCurrencyDropdown(v => !v)} aria-expanded={showCurrencyDropdown}>
                      {(() => {
                        const currentCurrencyObj = currencies.find(c => c.code === activeCurrencyCode);
                        return currentCurrencyObj ? `${activeCurrencyCode} (${currentCurrencyObj.symbol || activeCurrencyCode})` : activeCurrencyCode;
                      })()}
                      <span className="r4l-caret" aria-hidden="true"></span>
                    </button>
                    {showCurrencyDropdown && (
                      <div className="r4l-currency-menu">
                        {currenciesLoading ? (
                          <div className="r4l-currency-status">{t('destinationDetail.loading')}</div>
                        ) : currenciesError ? (
                          <div className="r4l-currency-status is-error">{currenciesError}</div>
                        ) : (
                          <ul>
                            {currencies.map((currency) => (
                              <li
                                key={currency.code}
                                className={selectedCurrency === currency.code ? 'active' : ''}
                                onClick={async () => {
                                  setSelectedCurrency(currency.code);
                                  setUserChangedCurrency(true);
                                  localStorage.setItem('selectedCurrency', currency.code);
                                  setShowCurrencyDropdown(false);

                                  await recalculateCartItemsInNewCurrency(currency.code);
                                  window.dispatchEvent(new Event('cartUpdated'));
                                }}
                              >
                                {currency.code} ({currency.symbol || currency.code})
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                  <button type="button" className="r4l-btn" onClick={handleSubmit} disabled={addDisabled}>
                    <CartIcon /> {t('destinationDetail.addToCart')}
                  </button>
                  <div className="r4l-pay-row">
                    <SecurePayments />
                  </div>
                </div>
              </div>
            </section>

            <div className="r4l-perk-row">
              <article><BoltIcon /><div>{t('r4l.detail.perk1')}<span>{t('r4l.detail.perk1Sub')}</span></div></article>
              <article><SignalIcon /><div>{t('r4l.detail.perk2')}<span>{t('r4l.detail.perk2Sub')}</span></div></article>
              <article><PhoneIcon /><div>{t('r4l.detail.perk3')}<span>{t('r4l.detail.perk3Sub')}</span></div></article>
              <article><ShieldIcon /><div>{t('r4l.detail.perk4')}<span>{t('r4l.detail.perk4Sub')}</span></div></article>
            </div>

            {/* Coverage card (no carrier logos: we don't hold per-country carrier data) */}
            <section className="r4l-network-card">
              <div className="r4l-network-copy">
                <p className="r4l-kicker">{t('r4l.detail.networksKicker')}</p>
                <h2 className="r4l-block-title r4l-block-title-xs">{t('r4l.detail.networksTitle', { country: countryName })}</h2>
                <p className="r4l-intro">{t('r4l.detail.networksSub')}</p>
              </div>
              <div className="r4l-network-photo">
                <img src={getDestinationHeroImage(countryIso3)} alt="" loading="lazy" />
                <Scribble text={t('scribbles.network')} />
              </div>
            </section>

            <div className="r4l-split">
              <section className="r4l-soft-card">
                <h2>{t('r4l.detail.vsTitle')}</h2>
                <div className="r4l-vs">
                  <div className="r4l-vs-row">
                    <span>Roam4Less</span>
                    <div className="r4l-track"><span style={{ width: '100%' }}></span></div>
                    <b>{t('r4l.detail.vsOurs').split('\n').map((l, i) => <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>)}</b>
                  </div>
                  <div className="r4l-vs-row">
                    <span>{t('promise.other')}</span>
                    <div className="r4l-track is-slow"><span></span></div>
                    <small>{t('r4l.detail.vsOther').split('\n').map((l, i) => <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>)}</small>
                  </div>
                </div>
                <p className="r4l-disclaimer">{t('r4l.bundlePromise.note')}</p>
              </section>
              <section className="r4l-soft-card r4l-why">
                <h2>{t('r4l.detail.whyTitle')}</h2>
                <div className="r4l-why-grid">
                  <article><GlobeIcon /><div><b>{t('r4l.detail.why1')}</b><span>{t('r4l.detail.why1Sub')}</span></div></article>
                  <article><SignalIcon /><div><b>{t('r4l.detail.why2')}</b><span>{t('r4l.detail.why2Sub')}</span></div></article>
                  <article><StarIcon /><div><b>{t('r4l.detail.why3')}</b><span>{t('r4l.detail.why3Sub')}</span></div></article>
                  <article><HeadsetIcon /><div><b>{t('r4l.detail.why4')}</b><span>{t('r4l.detail.why4Sub')}</span></div></article>
                </div>
              </section>
            </div>

            {/* FAQ */}
            <section className="r4l-dest-faq">
              <div className="r4l-section-head">
                <h2 className="r4l-block-title r4l-block-title-sm">{t('destinationDetail.faq.title', { country: countryName })}</h2>
                <Link className="r4l-btn-premium" to="/Faq">{t('r4l.detail.allQuestions')}<i aria-hidden="true"></i></Link>
              </div>
              <div className="r4l-faq-grid">
                <div>{faqItems.slice(0, 4).map(renderFaq)}</div>
                <div>{faqItems.slice(4).map(renderFaq)}</div>
              </div>
            </section>

            {/* More destinations */}
            {moreCountries.length > 0 && (
              <section className="r4l-dest-more-section">
                <div className="r4l-section-head">
                  <h2 className="r4l-block-title r4l-block-title-sm">{t('r4l.detail.moreTitle')}</h2>
                  <Link className="r4l-btn-premium" to="/destination">{t('View all destinations')}<i aria-hidden="true"></i></Link>
                </div>
                <div className="r4l-dest-grid is-four">
                  {moreCountries.filter((c) => c.iso3 !== countryIso3).slice(0, 4).map((country) => {
                    const photo = getDestinationCardImage(country.iso3);
                    const flag = country?.flag || window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg');
                    return (
                      <article className="r4l-dest-card" key={country.iso3}>
                        <Link to={`/destination-detail/${country.iso3}`} state={{ countryData: country }}>
                          <div className={`r4l-dest-photo${photo ? '' : ' is-flag'}`}>
                            <div className="r4l-dest-photo-clip">
                              <img className="r4l-cover" src={photo || flag.replace('/w80/', '/w320/')} alt={`${country.name} eSIM`} loading="lazy" />
                            </div>
                            {photo && <img className="r4l-flag" src={flag} alt="" />}
                          </div>
                          <div className="r4l-dest-meta">
                            <div>
                              <h3>{country.name}</h3>
                              <p>{t('perGB')}: <b>{country?.symbol || '$'}{country?.lowest_rate}</b> {country?.currency || 'USD'}</p>
                            </div>
                            <span className="r4l-arrow" aria-hidden="true">→</span>
                          </div>
                        </Link>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            <div className="r4l-trustbar">
              <div><BoltIcon />{t('r4l.detail.trust1')}</div>
              <div><ShieldIcon />{t('r4l.detail.trust2')}</div>
              <div><PhoneIcon />{t('r4l.detail.trust3')}</div>
              <div><StarIcon />{t('r4l.detail.trust4')}</div>
              <div><HeadsetIcon />{t('r4l.detail.trust5')}</div>
            </div>
          </div>

          {/* Mobile sticky buy bar */}
          <div className="r4l-mobile-bar">
            <div>
              <b>{priceLoading ? t('destinationDetail.calculating') : priceText}</b>
              <span>{countryName}</span>
            </div>
            <button type="button" className="r4l-btn" onClick={handleSubmit} disabled={addDisabled}>
              {t('destinationDetail.addToCart')}
            </button>
          </div>
        </>
      )}

      <Modal
        isOpen={showDeviceModal}
        onRequestClose={closeDeviceModal}
        style={customStyles}
        contentLabel="Device Compatibility"
        portalClassName="device-compatibility-modal"
      >
        <div className="paymentsuccess compatibilitymodal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {isShowingChildDevices && selectedDevice && compatibilityData.length > 0 && compatibilityData[0].name !== null
                  ? t('destinationDetail.modal.chooseDevice', { device: selectedDevice?.name })
                  : isShowingChildDevices && compatibilityData.length > 0 && compatibilityData[0].name === null
                    ? ''
                    : t('destinationDetail.modal.checkCompatibility')
                }
              </h3>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeDeviceModal();
                }}
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                style={{
                  position: 'absolute',
                  top: '33px',
                  right: '36px',
                  background: 'none',
                  border: 'none',
                  padding: '5px',
                  cursor: 'pointer',
                  zIndex: 1002,
                  pointerEvents: 'auto'
                }}
              >
                <img src={window.getPublicUrl('images/close.svg')} alt="Close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-12">
                  {!isShowingChildDevices && (
                    <p className='mb-3'>{t('destinationDetail.modal.deviceNotListed')}</p>
                  )}

                  <div className="device-compatibility-content">
                    {compatibilityLoading ? (
                      <div className="loader-overlay">
                        <span className="loader"></span>
                      </div>
                    ) : compatibilityData.length === 0 ? (
                      <div className="text-center">
                        <h4>{t('destinationDetail.modal.noDataAvailable')}</h4>
                      </div>
                    ) : (
                      <div className="device-buttons-container mt-3">
                        {compatibilityData.map((device, index) => (
                          device.name === null && device.description ? (
                            <div key={device.id || index} className="compatibility-description" style={{
                              padding: '20px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef',
                              marginBottom: '20px'
                            }}>
                              <div style={{
                                whiteSpace: 'pre-line',
                                lineHeight: '1.6',
                                color: '#333',
                                fontSize: '14px'
                              }}>
                                {device.description}
                              </div>
                            </div>
                          ) : (
                            <button
                              key={device.id || index}
                              className="device-compatibility-btn"
                              onClick={() => {
                                handleDeviceSelection(device);
                              }}
                              style={{
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                                e.target.style.borderColor = '#ddd';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#fff';
                                e.target.style.borderColor = '#ddd';
                              }}
                            >
                              {device.name}
                              <span style={{
                                position: 'absolute',
                                right: '14px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#ddd',
                                fontSize: '12px'
                              }}>
                                →
                              </span>
                            </button>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0 justify-content-start">
              {isShowingChildDevices && (
                <button
                  type="button"
                  className="btn btn-secondary me-2 "
                  onClick={handleBackToParent}
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: '#6c757d',
                    color: '#000',
                    padding: '8px 20px',
                    borderRadius: '23px',
                    border: '1px solid #6c757d',
                    cursor: 'pointer'
                  }}
                >
                  {t('destinationDetail.modal.back')}
                </button>
              )}
              <button
                type="button"
                className="btn view-all get-sim m-0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeDeviceModal();
                }}
                style={{
                  width: '54px',
                  padding: '9px',
                  fontSize: '14px'
                }}
              >
                {t('destinationDetail.modal.ok')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default DestinationDetail;